import { useParams, Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowLeft, ChevronDown, Mail, MessageCircle, LifeBuoy, FileText, Shield, Phone } from "lucide-react";
import { Logo } from "../components/Logo";

export const COMPANY = {
  legalName: "JackCash LLC",
  brand: "Jack Cash",
  domain: "www.jackcashpay.com",
  supportEmail: "contact@jackcash.us",
  supportPhone: "",
  legalEmail: "contact@jackcash.us",
  jurisdiction: "the United States",
  entityType: "a limited liability company",
  formed: "December 28, 2025",
  updated: "December 28, 2025",
};

function Shell({ icon: Icon, title, children }) {
  const nav = useNavigate();
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="min-h-screen w-full flex justify-center bg-background">
      <div className="w-full max-w-2xl min-h-screen px-6 pt-8 pb-16">
        <div className="flex items-center justify-between mb-8">
          <button onClick={() => (window.history.length > 1 ? nav(-1) : nav("/"))} data-testid="legal-back" className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground">
            <ArrowLeft size={18} /> Back
          </button>
          <Link to="/"><Logo size={30} /></Link>
        </div>
        <div className="flex items-center gap-3 mb-2">
          <span className="grid place-items-center w-11 h-11 rounded-2xl bg-primary/10 text-primary"><Icon size={22} /></span>
          <h1 className="font-display text-3xl font-light tracking-tight">{title}</h1>
        </div>
        <p className="text-xs text-muted-foreground mb-8">Last updated {COMPANY.updated} · {COMPANY.legalName}</p>
        <div className="space-y-6 text-[15px] leading-relaxed text-foreground/90">{children}</div>
        <div className="mt-12 pt-6 border-t border-border flex flex-wrap gap-4 text-sm">
          <Link to="/legal/about" className="text-primary font-semibold">About</Link>
          <Link to="/legal/terms" className="text-primary font-semibold">Terms</Link>
          <Link to="/legal/privacy" className="text-primary font-semibold">Privacy</Link>
          <Link to="/legal/faq" className="text-primary font-semibold">FAQ</Link>
          <Link to="/legal/support" className="text-primary font-semibold">Support</Link>
        </div>
      </div>
    </div>
  );
}

const Section = ({ n, title, children }) => (
  <section data-testid={`legal-section-${n}`}>
    <h2 className="font-semibold text-lg mb-2">{n ? `${n}. ` : ""}{title}</h2>
    <div className="space-y-2 text-muted-foreground">{children}</div>
  </section>
);

function Terms() {
  return (
    <Shell icon={FileText} title="Terms of Service">
      <p>These Terms of Service ("Terms") govern your access to and use of the {COMPANY.brand} digital wallet application and related services (the "Service") operated by {COMPANY.legalName}, {COMPANY.entityType} formed on {COMPANY.formed} ("{COMPANY.brand}", "we", "us"). By creating an account or using the Service, you agree to these Terms.</p>
      <Section n="1" title="Eligibility & Accounts">
        <p>You must be at least 18 years old and able to form a binding contract to use {COMPANY.brand}. You agree to provide accurate information, keep your credentials and payment PIN confidential, and are responsible for all activity on your account.</p>
      </Section>
      <Section n="2" title="The Wallet & Balances">
        <p>Your {COMPANY.brand} balance reflects funds recorded on our ledger. Balances are held in US dollars and are not a bank deposit and do not earn interest. Every transaction is recorded immutably; your balance only changes after a transaction is confirmed.</p>
      </Section>
      <Section n="3" title="Sending, Receiving & Fees">
        <p>You may send and receive money using a Cash Tag, phone number or email where available. You authorize us to debit your balance for transfers, applicable fees, and subscriptions you enable. Transfer limits and fees are shown before you confirm.</p>
      </Section>
      <Section n="4" title="Deposits & Withdrawals">
        <p>Deposits are processed by third-party payment providers (such as Stripe). We credit your balance only after the provider confirms the payment. Withdrawals and external cash-out methods may be subject to verification, limits, and provider availability.</p>
      </Section>
      <Section n="5" title="Identity Verification (KYC)">
        <p>To access certain features you must complete identity verification. We may request documents and may suspend or limit features until verification is complete, as required to prevent fraud and comply with applicable law.</p>
      </Section>
      <Section n="6" title="Prohibited Use">
        <p>You may not use {COMPANY.brand} for unlawful activity, fraud, money laundering, funding of illegal goods, or any activity that violates these Terms or applicable law. We may freeze, review, or close accounts and transactions we reasonably believe are suspicious.</p>
      </Section>
      <Section n="7" title="Subscriptions & Verified Badges">
        <p>Optional paid features, such as verified badges, renew automatically from your wallet balance until cancelled. You can cancel at any time; cancellation stops future renewals and does not refund the current period.</p>
      </Section>
      <Section n="8" title="Disclaimers & Liability">
        <p>The Service is provided "as is" without warranties of any kind. To the maximum extent permitted by law, {COMPANY.legalName} is not liable for indirect, incidental, or consequential damages. Nothing in these Terms limits liability that cannot be limited by law.</p>
      </Section>
      <Section n="9" title="Changes & Governing Law">
        <p>We may update these Terms and will post the new effective date. Continued use means you accept the changes. These Terms are governed by the laws of {COMPANY.jurisdiction}. Questions: {COMPANY.legalEmail}.</p>
      </Section>
    </Shell>
  );
}

function Privacy() {
  return (
    <Shell icon={Shield} title="Privacy Policy">
      <p>{COMPANY.legalName} ("we") respects your privacy. This Policy explains what we collect, how we use it, and your choices when you use {COMPANY.brand}.</p>
      <Section n="1" title="Information We Collect">
        <p>Account details (name, username, email, phone, country), identity verification documents, device and session information, transaction history, and support messages. We do not store full card numbers; payments are handled by our payment providers.</p>
      </Section>
      <Section n="2" title="How We Use Information">
        <p>To operate your wallet, process transactions, verify identity, prevent fraud, provide support, send receipts and notifications, and comply with legal obligations.</p>
      </Section>
      <Section n="3" title="Sharing">
        <p>We share data with service providers that help us run {COMPANY.brand} (such as payment processors and email providers), and with authorities when required by law. We do not sell your personal information.</p>
      </Section>
      <Section n="4" title="Security">
        <p>We protect your account with encryption in transit, hashed passwords and PINs, session controls, and an optional payment PIN. No system is perfectly secure, so please safeguard your credentials.</p>
      </Section>
      <Section n="5" title="Your Rights">
        <p>You may access, correct, or delete your account information, subject to records we must retain by law. Contact {COMPANY.legalEmail} to exercise your rights.</p>
      </Section>
      <Section n="6" title="Retention & Changes">
        <p>We retain information as long as needed to provide the Service and meet legal requirements. We may update this Policy and will post the new effective date.</p>
      </Section>
    </Shell>
  );
}

const FAQS = [
  ["What is Jack Cash?", "Jack Cash is a digital wallet to send, receive, request and save money instantly using a unique Cash Tag. Balances are in US dollars and tracked on a real, auditable ledger."],
  ["How do I add money?", "Open Add Money and pay with a card, Apple Pay or Google Pay through our secure Stripe checkout. Your balance updates once the payment is confirmed."],
  ["Is there a fee to send money?", "Sending money to another Cash Tag is free. Any applicable fees for deposits or other services are always shown before you confirm."],
  ["What is a Cash Tag?", "Your Cash Tag is your unique $handle (e.g. $jack) that others use to pay you. You can also share your QR code from the Receive screen."],
  ["How do I protect my account?", "Set a payment PIN in Security, turn on 'require PIN for payments', review your active devices, and never share your password or PIN with anyone."],
  ["What is identity verification (KYC)?", "For certain features like sending to a phone or email, we verify your identity to keep the platform safe and compliant. It takes just a few minutes."],
  ["Can I schedule recurring payments?", "Yes. Use Recurring to auto-send money weekly or monthly, and pause or cancel any time."],
  ["How do referrals work?", "Share your referral link. When a friend signs up and makes their first deposit, you both earn a reward credited to your wallet."],
];

function FAQ() {
  const [open, setOpen] = useState(0);
  return (
    <Shell icon={LifeBuoy} title="Frequently Asked Questions">
      <div className="space-y-3">
        {FAQS.map(([q, a], i) => (
          <div key={i} data-testid={`faq-item-${i}`} className="rounded-2xl bg-card border border-border overflow-hidden">
            <button onClick={() => setOpen(open === i ? -1 : i)} data-testid={`faq-toggle-${i}`}
              className="w-full flex items-center justify-between gap-3 p-4 text-left font-semibold">
              <span>{q}</span>
              <ChevronDown size={18} className={`shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`} />
            </button>
            {open === i && <div className="px-4 pb-4 text-muted-foreground text-sm -mt-1">{a}</div>}
          </div>
        ))}
      </div>
    </Shell>
  );
}

function Support() {
  return (
    <Shell icon={MessageCircle} title="Support">
      <p>We're here to help, day or night. Most answers are in our <Link to="/legal/faq" className="text-primary font-semibold">FAQ</Link>. If you still need a hand, reach out through any option below and a real person will take care of you.</p>

      <div className="flex items-start gap-4 p-4 rounded-2xl bg-primary/5 border border-primary/20">
        <span className="grid place-items-center w-12 h-12 rounded-2xl bg-primary/10 text-primary shrink-0"><MessageCircle size={22} /></span>
        <span>
          <span className="block font-semibold">24/7 live chat with a real human</span>
          <span className="block text-sm text-muted-foreground">Open the Jack Cash app, tap the assistant, then “Talk to a person”. An agent connects with you around the clock — 24 hours a day, 7 days a week.</span>
        </span>
      </div>

      <a href={`mailto:${COMPANY.supportEmail}`} data-testid="support-email-link"
        className="flex items-center gap-4 p-4 rounded-2xl bg-card border border-border transition-transform active:scale-[0.98]">
        <span className="grid place-items-center w-12 h-12 rounded-2xl bg-primary/10 text-primary shrink-0"><Mail size={22} /></span>
        <span>
          <span className="block font-semibold">Email support</span>
          <span className="block text-sm text-muted-foreground">{COMPANY.supportEmail} · we reply within 24 hours</span>
        </span>
      </a>

      {COMPANY.supportPhone && (
        <a href={`tel:${COMPANY.supportPhone.replace(/[^+\d]/g, "")}`} data-testid="support-phone-link"
          className="flex items-center gap-4 p-4 rounded-2xl bg-card border border-border transition-transform active:scale-[0.98]">
          <span className="grid place-items-center w-12 h-12 rounded-2xl bg-primary/10 text-primary shrink-0"><Phone size={22} /></span>
          <span>
            <span className="block font-semibold">Phone support</span>
            <span className="block text-sm text-muted-foreground">{COMPANY.supportPhone} · available 24/7</span>
          </span>
        </a>
      )}

      <div className="rounded-2xl bg-card border border-border p-4 text-sm text-muted-foreground">
        <span className="block font-semibold text-foreground mb-1">Hours & response times</span>
        Live chat: 24/7 · Email: replies within 24 hours · Typical in-app chat wait: a few minutes.
      </div>

      <p className="text-sm text-muted-foreground">For legal or privacy matters, contact {COMPANY.legalEmail}. {COMPANY.legalName}, {COMPANY.jurisdiction}.</p>

      <p className="text-xs text-muted-foreground border-t border-border pt-4 mt-2">
        Jack Cash is a financial technology company, not a bank. Banking and payment services are provided by our licensed partner institutions. Some optional features and products may carry fees, which are always shown before you confirm.
      </p>
    </Shell>
  );
}

function About() {
  return (
    <Shell icon={Shield} title="About Jack Cash">
      <p className="text-lg">{COMPANY.brand} is a premium digital wallet built to make moving money simple, fast and secure — with a personal Cash Tag at the center of it all.</p>

      <Section n="" title="Our mission">
        <p>We believe everyone deserves modern, transparent money tools — without hidden fees or confusing fine print. {COMPANY.brand} lets you send, receive, request and split money in seconds, save toward goals, convert between USD and HTG, and spend with virtual and physical cards, all from one clean app.</p>
      </Section>

      <Section n="" title="A fintech, not a bank">
        <p>{COMPANY.legalName} is a financial technology company, not a bank. Payment and banking services are provided by <strong>Stripe, Inc. and its partner banks</strong>. Funds and card services are delivered through our regulated partners, while {COMPANY.brand} focuses on building a delightful, secure product experience on top.</p>
      </Section>

      <Section n="" title="Security first">
        <p>Every money movement runs on a real, auditable ledger with server-side authorization, idempotent transactions, a payment PIN, two-factor authentication, trusted-device checks, and optional 24/7 AI fraud monitoring with Account Guard.</p>
      </Section>

      <Section n="" title="Here for you, 24/7">
        <p>Need a hand? Chat with our in-app assistant or reach a real human on our support team, any time of day. See <Link to="/legal/support" className="text-primary font-semibold">support options</Link>.</p>
      </Section>

      <Section n="" title="Company">
        <p>{COMPANY.legalName}<br />Cheyenne, Wyoming, {COMPANY.jurisdiction}<br />Email: <a href={`mailto:${COMPANY.supportEmail}`} className="text-primary font-semibold">{COMPANY.supportEmail}</a><br />Web: <a href={`https://${COMPANY.domain}`} className="text-primary font-semibold">{COMPANY.domain}</a></p>
      </Section>

      <p className="text-xs text-muted-foreground border-t border-border pt-4 mt-2">
        {COMPANY.brand} is a financial technology company, not a bank. Payment and banking services are provided by Stripe, Inc. and its partner banks. Some optional features and products may carry fees, which are always shown before you confirm.
      </p>
    </Shell>
  );
}

export default function Legal() {
  const { doc } = useParams();
  if (doc === "about") return <About />;
  if (doc === "privacy") return <Privacy />;
  if (doc === "faq") return <FAQ />;
  if (doc === "support") return <Support />;
  return <Terms />;
}
